/** Earcon variants: turn boundary vs barge-in hard stop. */
export type CueKind = 'turn' | 'interrupt';

/**
 * Soft earcon. Returns approximate duration in ms so callers can hold playback bias across the sound.
 * - turn: ascending F5→C6 (~220 ms) — listening committed / natural end of assistant audio.
 * - interrupt: descending C5→G4 (~160 ms) — barge-in hard stop only.
 */
export function playCueTone(
  ctx: AudioContext,
  destination: AudioNode,
  kind: CueKind = 'turn',
  volume?: number,
): number {
  if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
  const t0 = ctx.currentTime + 0.012;
  if (kind === 'interrupt') {
    const vol = volume ?? 0.11;
    const notes = [
      { f: 523.25, at: 0, dur: 0.08 }, // C5
      { f: 392.0, at: 0.055, dur: 0.1 }, // G4
    ];
    scheduleNotes(ctx, destination, t0, notes, vol);
    return 160;
  }
  const vol = volume ?? 0.14;
  const notes = [
    { f: 698.46, at: 0, dur: 0.11 }, // F5
    { f: 1046.5, at: 0.07, dur: 0.13 }, // C6
  ];
  scheduleNotes(ctx, destination, t0, notes, vol);
  return 220;
}

function scheduleNotes(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  notes: { f: number; at: number; dur: number }[],
  volume: number,
) {
  for (const n of notes) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = n.f;
    const start = t0 + n.at;
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(volume, start + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, start + n.dur);
    osc.connect(g);
    g.connect(destination);
    osc.start(start);
    osc.stop(start + n.dur + 0.02);
  }
}
